import axios from "axios";

const STORAGE_KEY = "authorization-cache";
const STORAGE_VERSION = 1;
const API_URL = process.env.REACT_APP_API_URL;

function lower(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeRole(role) {
  if (!role) return null;
  if (typeof role === "string") {
    return {
      roleCode: role,
      roleName: role,
    };
  }

  return {
    ...role,
    roleId: role.roleId ?? role.RoleId,
    roleCode: role.roleCode ?? role.RoleCode ?? role.roleName ?? role.RoleName,
    roleName: role.roleName ?? role.RoleName ?? role.roleCode ?? role.RoleCode,
    description: role.description ?? role.Description,
  };
}

function normalizeApplication(application) {
  if (!application) return null;

  return {
    ...application,
    applicationId: application.applicationId ?? application.ApplicationId,
    applicationCode: application.applicationCode ?? application.ApplicationCode,
    applicationName: application.applicationName ?? application.ApplicationName,
    accessPermissionCode: application.accessPermissionCode ?? application.AccessPermissionCode,
    route: application.route ?? application.Route,
    // icon: application.icon ?? application.Icon,
    description: application.description ?? application.Description,
    displayOrder: application.displayOrder ?? application.DisplayOrder ?? 0,
  };
}

export function normalizeAuthorization(value = {}) {
  const user = value.user || value.User || null;
  const roles = (value.roles || value.Roles || []).map(normalizeRole).filter(Boolean);
  const permissions = (value.permissions || value.Permissions || [])
    .map((permission) => String(permission || "").trim())
    .filter(Boolean);
  const applications = (value.applications || value.Applications || [])
    .map(normalizeApplication)
    .filter(Boolean);

  return {
    version: STORAGE_VERSION,
    loadedAt: value.loadedAt || new Date().toISOString(),
    user,
    isAdmin: Boolean(value.isAdmin ?? value.IsAdmin),
    roles,
    permissions,
    applications,
  };
}

export function readAuthorizationCache() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const cached = normalizeAuthorization(JSON.parse(raw));
    return cached.version === STORAGE_VERSION ? cached : null;
  } catch {
    return null;
  }
}

export function writeAuthorizationCache(value) {
  const authorization = normalizeAuthorization(value);

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(authorization));
    window.dispatchEvent(
      new CustomEvent("authorization:changed", {
        detail: authorization,
      }),
    );
  } catch {
    // Cache is an optimization only.
  }

  return authorization;
}

export function clearAuthorizationCache() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent("authorization:changed", { detail: null }));
  } catch {
    // Cache is an optimization only.
  }
}

export async function getAuthorization() {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const response = await axios.get(`${API_URL}/api/me/authorization`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  return normalizeAuthorization(response.data);
}

export async function loadAuthorization({ force = false } = {}) {
  if (!force) {
    const cached = readAuthorizationCache();
    if (cached) return cached;
  }

  const authorization = await getAuthorization();
  return writeAuthorizationCache(authorization);
}

export function getCachedAuthorization() {
  return readAuthorizationCache();
}

export function hasPermission(permissionCode, authorization = readAuthorizationCache()) {
  if (!permissionCode || !authorization) return false;

  const target = lower(permissionCode);
  return authorization.permissions.some((permission) => lower(permission) === target);
}

export function hasAnyPermission(permissionCodes = [], authorization = readAuthorizationCache()) {
  if (!authorization) return false;
  return permissionCodes.some((permissionCode) => hasPermission(permissionCode, authorization));
}

export function hasAllPermissions(permissionCodes = [], authorization = readAuthorizationCache()) {
  if (!authorization) return false;
  return permissionCodes.every((permissionCode) => hasPermission(permissionCode, authorization));
}

export function hasRole(roleCode, authorization = readAuthorizationCache()) {
  if (!roleCode || !authorization) return false;
  if (authorization.isAdmin && lower(roleCode) === "admin") return true;

  const target = lower(roleCode);
  return authorization.roles.some(
    (role) => lower(role.roleCode) === target || lower(role.roleName) === target,
  );
}

export function hasApplication(applicationCode, authorization = readAuthorizationCache()) {
  if (!applicationCode || !authorization) return false;

  const target = lower(applicationCode);
  return authorization.applications.some(
    (application) =>
      lower(application.applicationCode) === target ||
      lower(application.accessPermissionCode) === target,
  );
}

const authorizationService = {
  clearAuthorizationCache,
  getAuthorization,
  getCachedAuthorization,
  hasAllPermissions,
  hasAnyPermission,
  hasApplication,
  hasPermission,
  hasRole,
  loadAuthorization,
  normalizeAuthorization,
  readAuthorizationCache,
  writeAuthorizationCache,
};

export default authorizationService;
